import React from "react";

/* ================================================================
   Flat 2D Storyboard — 8 templates
   Clean flat-design narrative panels, dialogue, walk cycles,
   story arcs — optimized for storytelling & explanation
   ================================================================ */

// ── 1. Story Panel ───────────────────────────────────────────────────
export const StoryPanel: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const panels = (props.panels as string[]) ?? ["Beginning", "Rising Action", "Climax", "Resolution"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {panels.map((label, i) => {
        const cols = Math.min(panels.length, 4);
        const w = 720 / cols - 10;
        const x = 40 + i * (w + 10);
        const isActive = i === activeIndex;
        const isPast = i < activeIndex;
        return (
          <g key={i}>
            <rect x={x} y="60" width={w} height="300" rx="12" fill={isActive ? "#1e1b4b" : "#0f172a"} stroke={isActive ? "#6366f1" : isPast ? "#334155" : "#1e293b"} strokeWidth={isActive ? 3 : 1} />
            {/* Panel number */}
            <circle cx={x + 20} cy="80" r="12" fill={isActive ? "#6366f1" : "#334155"} />
            <text x={x + 20} y="85" textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold">{i + 1}</text>
            {/* Scene illustration placeholder */}
            <rect x={x + 15} y="100" width={w - 30} height={180} rx="6" fill={isActive ? "#1e293b" : "#0f172a"} />
            {/* Simple scene elements */}
            <circle cx={x + w / 2} cy="180" r="15" fill={isActive ? "#6366f1" : "#475569"} />
            <rect x={x + w / 2 - 8} y="195" width="16" height="25" rx="4" fill={isActive ? "#4f46e5" : "#334155"} />
            {/* Label */}
            <text x={x + w / 2} y="320" textAnchor="middle" fill={isActive ? "#e0e7ff" : "#64748b"} fontSize="12" fontWeight={isActive ? "bold" : "normal"}>{label}</text>
            {/* Active glow */}
            {isActive && (
              <rect x={x - 2} y="58" width={w + 4} height="304" rx="13" fill="none" stroke="#818cf8" strokeWidth="1" opacity="0.4">
                <animate attributeName="opacity" values="0.2;0.6;0.2" dur="2s" repeatCount="indefinite" />
              </rect>
            )}
          </g>
        );
      })}
      {/* Progress bar */}
      <rect x="40" y="400" width="720" height="4" rx="2" fill="#1e293b" />
      <rect x="40" y="400" width={720 * ((activeIndex + 1) / panels.length)} height="4" rx="2" fill="#6366f1">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />
      </rect>
      <text x="400" y="450" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Story Arc</text>
    </svg>
  );
};

// ── 2. Dialogue Bubble ───────────────────────────────────────────────
export const DialogueBubble: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const messages = (props.messages as string[]) ?? ["Hello!", "How are you?", "I'm learning!", "That's great!"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Person A */}
      <circle cx="150" cy="380" r="30" fill="#6366f1" />
      <circle cx="150" cy="365" r="4" fill="#fff" />
      <rect x="135" y="410" width="30" height="40" rx="8" fill="#4f46e5" />
      <text x="150" y="470" textAnchor="middle" fill="#94a3b8" fontSize="11">Speaker A</text>
      {/* Person B */}
      <circle cx="650" cy="380" r="30" fill="#ec4899" />
      <circle cx="650" cy="365" r="4" fill="#fff" />
      <rect x="635" y="410" width="30" height="40" rx="8" fill="#db2777" />
      <text x="650" y="470" textAnchor="middle" fill="#94a3b8" fontSize="11">Speaker B</text>
      {/* Dialogue bubbles */}
      {messages.slice(0, activeIndex + 1).map((msg, i) => {
        const isA = i % 2 === 0;
        const bx = isA ? 200 : 380;
        const by = 50 + i * 80;
        const opacity = i === activeIndex ? 1 : 0.5;
        return (
          <g key={i} opacity={opacity}>
            <rect x={bx} y={by} width="220" height="50" rx="20" fill={isA ? "#1e1b4b" : "#4a1942"} stroke={isA ? "#6366f1" : "#ec4899"} strokeWidth="1.5" />
            {/* Tail */}
            {isA ? (
              <polygon points={`${bx + 10},${by + 45} ${bx - 15},${by + 60} ${bx + 30},${by + 50}`} fill="#1e1b4b" />
            ) : (
              <polygon points={`${bx + 210},${by + 45} ${bx + 235},${by + 60} ${bx + 190},${by + 50}`} fill="#4a1942" />
            )}
            <text x={bx + 110} y={by + 30} textAnchor="middle" fill="#e0e7ff" fontSize="13">{msg}</text>
            {i === activeIndex && (
              <rect x={bx - 2} y={by - 2} width="224" height="54" rx="21" fill="none" stroke={isA ? "#818cf8" : "#f472b6"} strokeWidth="1" opacity="0.5">
                <animate attributeName="opacity" values="0.3;0.7;0.3" dur="1.5s" repeatCount="indefinite" />
              </rect>
            )}
          </g>
        );
      })}
    </svg>
  );
};

// ── 3. Character Walk Cycle ──────────────────────────────────────────
export const CharacterWalkCycle: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const x = 80 + animationProgress * 640;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Ground */}
      <line x1="40" y1="400" x2="760" y2="400" stroke="#334155" strokeWidth="2" />
      {/* Ground details */}
      {[100, 200, 350, 500, 650].map((gx, i) => (
        <line key={i} x1={gx} y1="400" x2={gx + 20} y2="400" stroke="#475569" strokeWidth="3" />
      ))}
      {/* Path marker dots */}
      {Array.from({ length: 8 }).map((_, i) => (
        <circle key={i} cx={80 + i * 92} cy="410" r="3" fill={80 + i * 92 < x ? "#6366f1" : "#1e293b"} />
      ))}
      {/* Character */}
      <g transform={`translate(${x},0)`}>
        {/* Head */}
        <circle cx="0" cy="320" r="18" fill="#fbbf24" />
        <circle cx="-4" cy="316" r="2.5" fill="#1e293b" />
        <circle cx="6" cy="316" r="2.5" fill="#1e293b" />
        <path d="M-3,326 Q2,330 7,326" stroke="#1e293b" strokeWidth="1.5" fill="none" />
        {/* Body */}
        <rect x="-12" y="338" width="24" height="30" rx="6" fill="#3b82f6" />
        {/* Arms swinging */}
        <line x1="-12" y1="345" x2="-28" y2="370" stroke="#fbbf24" strokeWidth="6" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values="-20,-12,345;20,-12,345;-20,-12,345" dur="0.6s" repeatCount="indefinite" />
        </line>
        <line x1="12" y1="345" x2="28" y2="370" stroke="#fbbf24" strokeWidth="6" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values="20,12,345;-20,12,345;20,12,345" dur="0.6s" repeatCount="indefinite" />
        </line>
        {/* Legs walking */}
        <line x1="-6" y1="368" x2="-14" y2="398" stroke="#1e293b" strokeWidth="7" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values="-15,-6,368;15,-6,368;-15,-6,368" dur="0.6s" repeatCount="indefinite" />
        </line>
        <line x1="6" y1="368" x2="14" y2="398" stroke="#1e293b" strokeWidth="7" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" values="15,6,368;-15,6,368;15,6,368" dur="0.6s" repeatCount="indefinite" />
        </line>
      </g>
      {/* Footprints behind */}
      {Array.from({ length: Math.floor(animationProgress * 6) }).map((_, i) => (
        <g key={i} opacity={0.2 + i * 0.05}>
          <ellipse cx={100 + i * 100} cy="405" rx="6" ry="3" fill="#475569" />
          <ellipse cx={115 + i * 100} cy="405" rx="6" ry="3" fill="#475569" />
        </g>
      ))}
      <text x="400" y="460" textAnchor="middle" fill="#94a3b8" fontSize="14">Walk Cycle — {Math.round(animationProgress * 100)}%</text>
    </svg>
  );
};

// ── 4. Narrative Arc ─────────────────────────────────────────────────
export const NarrativeArc: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const stages =
    (props.stages as string[]) ||
    (props.labels as string[]) ||
    ["Beginning", "Rising\nAction", "Climax", "Falling\nAction", "Resolution"];
  const points = [
    { x: 100, y: 350 }, { x: 250, y: 250 }, { x: 400, y: 100 }, { x: 550, y: 250 }, { x: 700, y: 350 },
  ];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Arc line */}
      <path d="M100,350 Q175,250 250,250 Q325,150 400,100 Q475,150 550,250 Q625,350 700,350" fill="none" stroke="#334155" strokeWidth="3" />
      {/* Filled progress */}
      {activeIndex > 0 && (
        <path d={`M100,350 ${activeIndex >= 1 ? "Q175,250 250,250" : ""} ${activeIndex >= 2 ? "Q325,150 400,100" : ""} ${activeIndex >= 3 ? "Q475,150 550,250" : ""} ${activeIndex >= 4 ? "Q625,350 700,350" : ""}`} fill="none" stroke="#6366f1" strokeWidth="4" />
      )}
      {/* Stage markers */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={i === activeIndex ? 14 : 10} fill={i <= activeIndex ? "#6366f1" : "#1e293b"} stroke={i === activeIndex ? "#818cf8" : "#334155"} strokeWidth="2">
            {i === activeIndex && <animate attributeName="r" values="12;16;12" dur="1.5s" repeatCount="indefinite" />}
          </circle>
          <text x={p.x} y={p.y + 35} textAnchor="middle" fill={i === activeIndex ? "#e0e7ff" : "#64748b"} fontSize="11" fontWeight={i === activeIndex ? "bold" : "normal"}>
            {stages[i].split("\n").map((line, li) => (
              <tspan key={li} x={p.x} dy={li === 0 ? 0 : 13}>{line}</tspan>
            ))}
          </text>
        </g>
      ))}
      {/* Tension label */}
      <text x="30" y="200" fill="#64748b" fontSize="11" transform="rotate(-90,30,200)">Tension ↑</text>
      <text x="400" y="450" textAnchor="middle" fill="#e0e7ff" fontSize="16" fontWeight="bold">Narrative Arc</text>
    </svg>
  );
};

// ── 5. Split Screen Compare ─────────────────────────────────────────
export const SplitScreenCompare: React.FC<Record<string, unknown>> = (props) => {
  const animationProgress = (props.animationProgress as number) ?? 0;
  const dividerX = 100 + animationProgress * 600;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Left side */}
      <rect x="40" y="40" width={dividerX - 42} height="380" rx="8" fill="#1e1b4b" />
      <text x={(40 + dividerX) / 2} y="70" textAnchor="middle" fill="#818cf8" fontSize="14" fontWeight="bold">Before</text>
      <rect x="60" y="90" width={Math.max(0, dividerX - 80)} height="100" rx="6" fill="#0f172a" />
      <circle cx={Math.min(150, dividerX - 30)} cy="140" r="20" fill="#475569" />
      <rect x="60" y="210" width={Math.max(0, dividerX - 80)} height="60" rx="4" fill="#1e293b" />
      {/* Right side */}
      <rect x={dividerX + 2} y="40" width={758 - dividerX} height="380" rx="8" fill="#042f2e" />
      <text x={(dividerX + 760) / 2} y="70" textAnchor="middle" fill="#5eead4" fontSize="14" fontWeight="bold">After</text>
      <rect x={dividerX + 20} y="90" width={Math.max(0, 740 - dividerX)} height="100" rx="6" fill="#0f172a" />
      <circle cx={Math.max(dividerX + 50, 460)} cy="140" r="20" fill="#10b981" />
      <rect x={dividerX + 20} y="210" width={Math.max(0, 740 - dividerX)} height="60" rx="4" fill="#064e3b" />
      {/* Divider */}
      <line x1={dividerX} y1="35" x2={dividerX} y2="425" stroke="#fff" strokeWidth="3" />
      <circle cx={dividerX} cy="230" r="12" fill="#fff" stroke="#334155" strokeWidth="2" />
      <text x={dividerX} y="234" textAnchor="middle" fill="#1e293b" fontSize="10" fontWeight="bold">↔</text>
      <text x="400" y="460" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Compare — Before & After</text>
    </svg>
  );
};

// ── 6. Book Page Turn ────────────────────────────────────────────────
export const BookPageTurn: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Book spine */}
      <rect x="395" y="60" width="10" height="360" fill="#854d0e" rx="2" />
      {/* Left page */}
      <rect x="100" y="60" width="295" height="360" rx="4" fill="#fef3c7" stroke="#d4a574" strokeWidth="1.5" />
      {/* Page lines */}
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={i} x1="120" y1={100 + i * 30} x2="370" y2={100 + i * 30} stroke="#d4a574" strokeWidth="0.5" opacity="0.3" />
      ))}
      <text x="245" y="85" textAnchor="middle" fill="#92400e" fontSize="14" fontWeight="bold">Page {activeIndex * 2 + 1}</text>
      <text x="245" y="180" textAnchor="middle" fill="#78350f" fontSize="12">Chapter content here...</text>
      {/* Right page */}
      <rect x="405" y="60" width="295" height="360" rx="4" fill="#fffbeb" stroke="#d4a574" strokeWidth="1.5" />
      {Array.from({ length: 10 }).map((_, i) => (
        <line key={i} x1="425" y1={100 + i * 30} x2="680" y2={100 + i * 30} stroke="#d4a574" strokeWidth="0.5" opacity="0.3" />
      ))}
      <text x="552" y="85" textAnchor="middle" fill="#92400e" fontSize="14" fontWeight="bold">Page {activeIndex * 2 + 2}</text>
      {/* Page turning effect */}
      <path d="M700,60 Q680,200 700,420 Q650,350 630,200 Q650,100 700,60" fill="#fef3c7" stroke="#d4a574" strokeWidth="1" opacity="0.6">
        <animateTransform attributeName="transform" type="rotate" values="0,700,240;-3,700,240;0,700,240" dur="4s" repeatCount="indefinite" />
      </path>
      {/* Page number */}
      <text x="400" y="450" textAnchor="middle" fill="#92400e" fontSize="12">— {activeIndex + 1} / 5 —</text>
    </svg>
  );
};

// ── 7. Montage Grid ──────────────────────────────────────────────────
export const MontageGrid: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const scenes = (props.scenes as string[]) ?? ["Scene 1", "Scene 2", "Scene 3", "Scene 4", "Scene 5", "Scene 6"];
  const colors = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {scenes.slice(0, 6).map((scene, i) => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const x = 50 + col * 240;
        const y = 40 + row * 210;
        const isActive = i === activeIndex;
        return (
          <g key={i}>
            <rect x={x} y={y} width="220" height="190" rx="10" fill={isActive ? "#1e1b4b" : "#0f172a"} stroke={isActive ? colors[i] : "#1e293b"} strokeWidth={isActive ? 3 : 1} />
            {/* Scene icon */}
            <circle cx={x + 110} cy={y + 80} r={isActive ? 25 : 20} fill={colors[i]} opacity={isActive ? 0.8 : 0.3}>
              {isActive && <animate attributeName="r" values="20;28;20" dur="2s" repeatCount="indefinite" />}
            </circle>
            <text x={x + 110} y={y + 130} textAnchor="middle" fill={isActive ? "#e0e7ff" : "#64748b"} fontSize="12">{scene}</text>
            {/* Scene number */}
            <text x={x + 15} y={y + 22} fill={colors[i]} fontSize="11" fontWeight="bold">{i + 1}</text>
          </g>
        );
      })}
    </svg>
  );
};

// ── 8. Infographic Timeline ──────────────────────────────────────────
export const InfographicTimeline: React.FC<Record<string, unknown>> = (props) => {
  const activeIndex = (props.activeIndex as number) ?? 0;
  const items = (props.items as string[]) ?? ["Step 1", "Step 2", "Step 3", "Step 4", "Step 5"];
  const colors = ["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#3b82f6"];
  return (
    <svg viewBox="0 0 800 500" style={{ width: "100%", opacity: (props.opacity as number) ?? 1 }}>
      {/* Central vertical line */}
      <line x1="400" y1="30" x2="400" y2="450" stroke="#334155" strokeWidth="3" />
      {/* Progress fill */}
      <line x1="400" y1="30" x2="400" y2={30 + (activeIndex / (items.length - 1)) * 420} stroke="#6366f1" strokeWidth="4" />
      {items.map((item, i) => {
        const y = 50 + i * (400 / (items.length - 1));
        const isLeft = i % 2 === 0;
        const isActive = i === activeIndex;
        const isPast = i < activeIndex;
        return (
          <g key={i}>
            {/* Node */}
            <circle cx="400" cy={y} r={isActive ? 12 : 8} fill={isPast || isActive ? colors[i % colors.length] : "#1e293b"} stroke={isActive ? "#fff" : "none"} strokeWidth="2">
              {isActive && <animate attributeName="r" values="10;14;10" dur="1.5s" repeatCount="indefinite" />}
            </circle>
            {/* Connector */}
            <line x1={isLeft ? 380 : 420} y1={y} x2={isLeft ? 250 : 550} y2={y} stroke={isPast || isActive ? colors[i % colors.length] : "#334155"} strokeWidth="1.5" />
            {/* Card */}
            <rect x={isLeft ? 100 : 460} y={y - 25} width="150" height="50" rx="8" fill={isActive ? "#1e1b4b" : "#0f172a"} stroke={isPast || isActive ? colors[i % colors.length] : "#1e293b"} strokeWidth="1.5" />
            <text x={isLeft ? 175 : 535} y={y + 5} textAnchor="middle" fill={isActive ? "#e0e7ff" : "#94a3b8"} fontSize="12" fontWeight={isActive ? "bold" : "normal"}>{item}</text>
          </g>
        );
      })}
    </svg>
  );
};
